import React, { createContext, useCallback, useContext, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { ROUTES } from '@constants/routes';

interface ElearningWpRemotePage {
  route: string;
  enabled: boolean;
}

const ELEARNING_WP_REMOTE_FLOW: ElearningWpRemotePage[] = [
  { route: ROUTES.EWR_OTP, enabled: true },
  { route: ROUTES.EWR_WELCOME, enabled: true },
  { route: ROUTES.EWR_REGISTER, enabled: true },
  { route: ROUTES.EWR_VIDEO, enabled: true },
  { route: ROUTES.EWR_FINAL, enabled: true }
];

interface ElearningWpRemoteFlowContextValue {
  currentPage: ElearningWpRemotePage | undefined;
  canGoNext: boolean;
  canGoBack: boolean;
  goNext: () => void;
  goBack: () => void;
  reset: () => void;
}

const ElearningWpRemoteFlowContext =
  createContext<ElearningWpRemoteFlowContextValue | null>(null);

interface ElearningWpRemoteFlowProviderProps {
  children: React.ReactNode;
}

function ElearningWpRemoteFlowProvider({
  children
}: ElearningWpRemoteFlowProviderProps) {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const allowedPages = useMemo(
    () => ELEARNING_WP_REMOTE_FLOW.filter((p) => p.enabled),
    []
  );
  const currentIndex = useMemo(
    () => allowedPages.findIndex((p) => p.route === pathname),
    [allowedPages, pathname]
  );

  const goNext = useCallback(() => {
    const next = allowedPages[currentIndex + 1];
    if (next) navigate(next.route);
  }, [allowedPages, currentIndex, navigate]);

  const goBack = useCallback(() => {
    const prev = allowedPages[currentIndex - 1];
    if (prev) navigate(prev.route);
  }, [allowedPages, currentIndex, navigate]);

  const reset = useCallback(() => {
    const start = allowedPages[0];
    if (start) navigate(ROUTES.EWR_WELCOME);
  }, [allowedPages, navigate]);

  const value = useMemo(
    () => ({
      currentPage: allowedPages[currentIndex],
      canGoNext: !!allowedPages[currentIndex + 1],
      canGoBack: !!allowedPages[currentIndex - 1],
      goNext,
      goBack,
      reset
    }),
    [allowedPages, currentIndex, goNext, goBack, reset]
  );

  return (
    <ElearningWpRemoteFlowContext.Provider value={value}>
      {children}
    </ElearningWpRemoteFlowContext.Provider>
  );
}

function useElearningWpRemoteFlow(): ElearningWpRemoteFlowContextValue {
  const ctx = useContext(ElearningWpRemoteFlowContext);
  if (!ctx)
    throw new Error(
      'useElearningWpRemoteFlow must be used within ElearningWpRemoteFlowProvider'
    );
  return ctx;
}

export { ElearningWpRemoteFlowProvider, useElearningWpRemoteFlow };
export type { ElearningWpRemoteFlowContextValue, ElearningWpRemotePage };
